import { combine, createEffect, createEvent, createStore } from 'effector';
import { PaginationProps } from 'antd/lib/pagination';
import { SorterResult } from 'antd/lib/table/interface';
import qs from 'qs';
import createPaginationStore from '@stores/pagination';
import createPreloadersStore from '@stores/preloaders';
import createFiltersStore from '@stores/filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import { LOCALE } from '@stores/localeWeight';
import api from '@constants/endpoints';
import request from '@helpers/request';
import { IGameRequest } from '@interfaces/IGameRequest';
import { get } from 'lodash';
import { closeModal } from '@stores/modals';
import { message } from 'antd';
import { updateGame } from '@stores/games';
import { clearSelectedGames } from '@stores/gamesRequest/selectedGames';

export const reset = createEvent('Games request store reset');

const defaultFilters = {
  sort: 'approved,asc',
};

export const getList = createEffect({
  name: 'getList',
  async handler(params?: {
    pagination?: PaginationProps;
    filters?: Record<string, any>;
    sorter?: SorterResult<IGameRequest>;
  }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    let filtersParams = filters.getState();

    const sorterParams: { sort?: string } = {};

    if (params?.sorter) {
      sorterParams.sort = params.sorter.order
        ? `${params.sorter.columnKey},${params.sorter.order.replace('end', '')}`
        : defaultFilters.sort;
    }

    if (params?.sorter && !params.filters) {
      setFilters({ ...filtersParams, ...sorterParams });
    }

    if (params?.filters) {
      setFilters({ ...params.filters, ...sorterParams });
      filtersParams = params.filters;
      paginationParams.current = 1;
    }

    const { current } = paginationParams;

    const requestParams: any = {
      paged: true,
      size: localStorage.getItem('gamesRequestPageSize'),
      page: (current || 1) - 1,
      ...defaultFilters,
      ...removeUndefinedFilters(filtersParams),
      ...sorterParams,
      paramsSerializer: (params: string[]) => qs.stringify(params, { arrayFormat: 'repeat' }),
    };
    if (requestParams.sort === defaultFilters.sort) {
      requestParams.sort = [defaultFilters.sort, 'id,desc'];
    } else {
      requestParams.sort = [requestParams.sort];
    }
    const currentLocale = localStorage.getItem('localeWeight') as LOCALE;
    const requestObj = {
      ...api.suggest.getList,
      headers: { 'Accept-Language': currentLocale },
    };
    return await request(requestObj, {
      params: requestParams,
    });
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'games',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

export const approve = createEffect({
  name: 'approve',
  async handler({ suggestId, code }: { suggestId: number; code: string }) {
    return await request(api.suggest.approve, {
      urlParams: {
        suggestId: suggestId.toString(),
      },
      data: {
        code,
      },
    });
  },
});

export const gameExist = createEffect({
  name: 'gameExist',
  async handler({
    suggestIds,
    duplicateGameId,
    isUseSynonym,
  }: {
    suggestIds: number[];
    duplicateGameId: number;
    isUseSynonym: boolean;
  }) {
    return await Promise.all(
      suggestIds.map((id) => {
        return request(api.suggest.gameExist, {
          urlParams: {
            suggestId: id.toString(),
          },
          params: {
            gameId: duplicateGameId.toString(),
            useSynonym: isUseSynonym,
          },
        });
      }),
    );
  },
});

export const reject = createEffect({
  name: 'reject',
  async handler({ suggestIds }: { suggestIds: number[] }) {
    return await Promise.all(
      suggestIds.map((id) => {
        return request(api.suggest.reject, {
          urlParams: {
            suggestId: id.toString(),
          },
        });
      }),
    );
  },
});

const preloaders = createPreloadersStore([getList, reject, gameExist, approve]);

const { filters, setFilters } = createFiltersStore({
  listName: 'suggest',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { filters, setFilters };

const list = createStore<IGameRequest[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'body', 'content'], []))
  .on(reject.done, (state) => {
    closeModal({ name: 'gameRequest' });
    const paginationNew = pagination.getState();
    if (state.length === 1) {
      paginationNew.current = Math.max((paginationNew.current || 0) - 1, 0);
    }
    getList({ pagination: pagination.getState() });
    clearSelectedGames();
  })
  .on(gameExist.done, (state) => {
    closeModal({ name: 'gameRequest' });
    getList({ pagination: pagination.getState() });
    clearSelectedGames();
  })
  .on(approve.done, (_, { result }) => {
    if (get(result, ['data', 'status', 'statusCodeValue'], 0) === 200) {
      getList({ pagination: pagination.getState() });
    } else {
      message.warn(get(result, ['data', 'status', 'body'], ''));
    }
  })
  .on(updateGame.done, () => {
    getList({ pagination: pagination.getState() });
  })
  .reset(reset);

const gamesRequest = combine({ preloaders, list, pagination, filters });

export default gamesRequest;
